import BlockIcon from "@mui/icons-material/Block";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { FormikTextField } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { proceedingParticipantsApi } from "@/api";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { QueryKey } from "@/query/Query";
import { Trans, useTranslation } from "@/translations";

import { useParticipantDeactivate } from "./hooks/useParticipantDeactivate";

interface Props {
  participantId: string;
  participantName: string;
  onClose: () => void;
  onDeactivated: () => void;
}

export function DeactivateParticipantDialog({ participantId, participantName, onClose, onDeactivated }: Props) {
  const { t } = useTranslation();
  const { formik } = useParticipantDeactivate(participantId, onDeactivated);

  const { data: countData } = useQuery({
    queryKey: [QueryKey.ProceedingParticipants, participantId, "active-claim-references-count"],
    queryFn: () => proceedingParticipantsApi.getProceedingParticipantActiveClaimReferencesCount({ participantId }),
  });

  return (
    <FormDialog
      title={t("proceedingParticipants.deactivate.dialogTitle")}
      onClose={onClose}
      formik={formik}
      cancelLabel={t("proceedingParticipants.deactivate.cancelButton")}
      submitLabel={t("proceedingParticipants.deactivate.confirmButton")}
      submitColor="error"
      submitStartIcon={<BlockIcon />}
      paperSx={{ maxWidth: { md: 600, lg: 600 } }}
    >
      <Stack spacing={2} pt={1}>
        <Typography variant="body2">
          <Trans
            i18nKey="proceedingParticipants.deactivate.dialogBody"
            values={{ participantName }}
            components={{ strong: <strong /> }}
          />
        </Typography>
        <FormikTextField
          formik={formik}
          fieldName="reason"
          label={t("proceedingParticipants.deactivate.reasonLabel")}
          placeholder={t("proceedingParticipants.deactivate.reasonPlaceholder")}
          multiline
          rows={3}
          required
        />
        {countData && countData.count > 0 && (
          <Alert severity="warning">
            <Box>
              <Trans
                i18nKey="proceedingParticipants.deactivate.activeClaimsWarning"
                count={countData.count}
                values={{ count: countData.count }}
                components={{ strong: <strong /> }}
              />
            </Box>
          </Alert>
        )}
      </Stack>
    </FormDialog>
  );
}
